import { Typography, Alert, Stack, Box, useTheme } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';

import { ReactComponent as IconLightbulb } from '../../../../assets/svg/lightbulb.svg';
import { HousekeepingInspection } from '../../api';
import { usePostInspection } from '../../hooks/use-post-inspection';
import { InspectionAction } from '../enum/inspection-action';
import { useGetInspections } from '../../hooks/use-get-inspections';
import { AssignedTime } from '../enum/inspection-assigned-time';
import { useFormatInspectionDates } from '../../hooks/use-format-inspection-dates';
import { CLEAN_TYPE_TRANSLATION_MAP } from '../enum/clean-type';

import { HousekeepingCard } from './housekeeping-card';

interface Props {
    className?: string;
    inspection: HousekeepingInspection;
    academicYear: string;
}

export const RequestedHousekeeping: FC<Props> = ({ className, inspection, academicYear }) => {
    const theme = useTheme();
    const { refetch } = useGetInspections(academicYear);
    const { t } = useTranslation();

    const { day, date, month } = useFormatInspectionDates(inspection);

    const { isLoading: isCancelling, mutate: cancelInspection } = usePostInspection(
        InspectionAction.CANCEL,
        { onSuccess: () => refetch() }
    );

    const preferredTimeLabel =
        inspection.preferred_cleaning_time === AssignedTime.Morning
            ? t('housekeeping_preferences_cleaning_time_morning')
            : t('housekeeping_preferences_cleaning_time_afternoon');

    const cleanTypeLabel = t(CLEAN_TYPE_TRANSLATION_MAP[inspection.clean_type!]);

    return (
        <HousekeepingCard
            className={className}
            inspection={inspection}
            statusBadge={
                <Alert
                    icon={false}
                    severity="success"
                    css={{
                        backgroundColor: theme.palette.success.light,
                        color: theme.palette.success.dark,
                        textTransform: 'uppercase',
                        borderRadius: 0,
                    }}
                >
                    {t('housekeeping_requested_badge')}
                </Alert>
            }
            title={`${day}, ${month} ${date}`}
            subtitle={t('housekeeping_requested_subtitle')}
            additionalInfo={
                <Box css={{ marginBottom: '16px', display: 'flex', gap: '10px' }}>
                    <Typography
                        variant="body1"
                        css={{ padding: '4px 10px', backgroundColor: theme.palette.grey[200] }}
                    >
                        {preferredTimeLabel}
                    </Typography>
                    <Typography
                        variant="body1"
                        css={{ padding: '4px 10px', backgroundColor: theme.palette.grey[200] }}
                    >
                        {cleanTypeLabel}
                    </Typography>
                </Box>
            }
        >
            <Stack direction="row" gap="16px" justifyContent="space-between">
                <Button
                    loading={isCancelling}
                    fullWidth
                    surrounding="light"
                    css={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'uppercase',
                    }}
                    onClick={() => cancelInspection({ id: inspection.id, academicYear })}
                >
                    {t('housekeeping_requested_action_cancel')}
                </Button>
            </Stack>
            <Alert
                severity="warning"
                css={{
                    backgroundColor: theme.palette.grey[200],
                    padding: '16px',
                    gap: '12px',
                    borderRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                }}
                icon={<IconLightbulb />}
            >
                {t('housekeeping_requested_confirmation_info')}
            </Alert>
        </HousekeepingCard>
    );
};
